/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/useAgGridKeyboardShortcuts.ts
 * FILE_ID: QB-AGGRID-KBD-001
 * PURPOSE: Wire AG-Grid lifecycle / keyboard events → Bravada handlers.
 *          Today:
 *           - Ctrl/Cmd + D fill-down across the active range
 *           - Right-click → onRowContextMenu(rowId, x, y)
 *          Future homes for Esc, Shift-Enter, Alt-Up/Down, etc.
 * WHY: lazy-aggrid-impl.tsx grew past the 300-line cap once MR 7
 *      added per-row context + selection plumbing. Pulling these
 *      handlers out keeps that module bounded and lets the
 *      shortcut logic be exercised independently.
 * FLOW:
 *   ┌─────────────────────┐   ┌────────────────────┐   ┌────────────────────┐
 *   │ AG-Grid cellKeyDown │ → │ detect Ctrl+D      │ → │ onFillDown(source, │
 *   │ event               │   │ + read getCellRange│   │   targets, fields) │
 *   └─────────────────────┘   └────────────────────┘   └────────────────────┘
 *   ┌──────────────────────┐  ┌────────────────────┐   ┌─────────────────────┐
 *   │ AG-Grid cellContext  │→ │ preventDefault +   │ → │ onRowContextMenu    │
 *   │ Menu event           │  │ read clientX/Y     │   │ (rowId, x, y)       │
 *   └──────────────────────┘  └────────────────────┘   └─────────────────────┘
 * RELATED:
 *   - QB-AGGRID-LAZY-001 (lazy-aggrid-impl.tsx) — consumer
 *   - QB-AGGRID-CTXMENU-001 — popup that consumes onRowContextMenu
 */

import { useCallback, type MutableRefObject } from 'react'
import type { QuoteLineItem } from '@/lib/quoteApi'

export interface UseAgGridKeyboardShortcutsArgs {
  /** Live ref to the grid api. Held in lazy-aggrid-impl's apiRef. */
  apiRef: MutableRefObject<
    | (import('ag-grid-community').GridApi<QuoteLineItem> | null)
    | (import('ag-grid-community').GridApi<QuoteLineItem>)
  >
  editableFieldIds: string[]
  onFillDown: (
    sourceItemId: string,
    targetItemIds: string[],
    fields: string[],
  ) => void
}

/**
 * Build a SAM-style right-click handler for AG-Grid. Returns a
 * memoised callback that prevents the browser context menu and
 * lifts (rowId, clientX, clientY) up to the section so it can
 * render its custom popup.
 */
export function useAgGridContextMenu(
  onRowContextMenu?: (rowId: string, x: number, y: number) => void,
) {
  return useCallback(
    (evt: { data?: QuoteLineItem | null; event?: Event | null }) => {
      if (!onRowContextMenu) return
      const native = evt.event as MouseEvent | undefined
      if (native) native.preventDefault()
      const id = evt.data?.id
      if (!id) return
      onRowContextMenu(id, native?.clientX ?? 0, native?.clientY ?? 0)
    },
    [onRowContextMenu],
  )
}

export function useAgGridKeyboardShortcuts(
  args: UseAgGridKeyboardShortcutsArgs,
) {
  const { apiRef, editableFieldIds, onFillDown } = args

  return useCallback(
    (evt: import('ag-grid-community').CellKeyDownEvent<QuoteLineItem>) => {
      const kb = evt.event as KeyboardEvent | undefined
      if (!kb) return
      // Ctrl/Cmd + D → fill-down across the active range. Mirrors
      // Excel's classic fill behaviour: top row of the range is the
      // source, every row below copies its values for each column in
      // the range. Cells outside ``editableFieldIds`` are skipped so
      // computed columns (item #, totals) stay clean.
      if (
        (kb.ctrlKey || kb.metaKey) &&
        (kb.key === 'd' || kb.key === 'D')
      ) {
        kb.preventDefault()
        const api = apiRef.current
        if (!api) return
        const ranges = api.getCellRanges?.() ?? []
        if (ranges.length === 0) return
        const range = ranges[0]
        const startIdx = Math.min(
          range.startRow?.rowIndex ?? 0,
          range.endRow?.rowIndex ?? 0,
        )
        const endIdx = Math.max(
          range.startRow?.rowIndex ?? 0,
          range.endRow?.rowIndex ?? 0,
        )
        const colIds = range.columns
          .map((c) => c.getColId())
          .filter((id) => editableFieldIds.includes(id))
        if (colIds.length === 0) return
        const sourceRow = api.getDisplayedRowAtIndex(startIdx)?.data
        if (!sourceRow?.id) return
        const targetIds: string[] = []
        for (let i = startIdx + 1; i <= endIdx; i++) {
          const r = api.getDisplayedRowAtIndex(i)?.data
          if (r?.id) targetIds.push(r.id)
        }
        if (targetIds.length === 0) return
        onFillDown(sourceRow.id, targetIds, colIds)
      }
    },
    [apiRef, editableFieldIds, onFillDown],
  )
}
